import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { GetProjectIdService } from '../getProjectId.service';
import { environment } from '../../environments/environment';
@Injectable({
  providedIn: 'root',
})
export class TenderingService {
  private apiUrl = environment.apiUrl;
  private subscription: Subscription = new Subscription();
  constructor(
    private http: HttpClient,
    private getProjectIdService: GetProjectIdService
  ) {}

  /**
   * ####################################
   * EXPRESSION OF INTEREST SECTION START
   * ####################################
   */
  // get list of EOI'S of individual project on developer side
  getEoiList(data: any): Observable<any> {
    let projectId: any;
    this.subscription = this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
      if (!projectId) {
        projectId = this.getProjectIdService.getProjectId();
      }
    });

    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/eoi/listing`,
      data
    );
  }
  // CollectData for EOI Inititiation
  getInitiatiationData(): Observable<any> {
    let projectId = this.getProjectIdService.getProjectServiceId();
    if (!projectId) {
      projectId = this.getProjectIdService.getProjectIdFromLocalStorage();
    }
    return this.http.get(`${this.apiUrl}/eoi-pre-initiation-data/${projectId}`);
  }

  // Initiate EOI
  initiateEOI(data: any): Observable<any> {
    let projectId = this.getProjectIdService.getProjectServiceId();
    if (!projectId) {
      projectId = this.getProjectIdService.getProjectIdFromLocalStorage();
    }
    return this.http.post(
      `${this.apiUrl}/e-o-i-organization-details/${projectId}`,
      data
    );
  }

  private eoiIdSubject = new BehaviorSubject<number | null>(null); // Initial value is null
  eoiId$ = this.eoiIdSubject.asObservable(); // Observable for other components to subscribe

  private pqqIdSubject = new BehaviorSubject<number | null>(null); // Initial value is null
  pqqId$ = this.pqqIdSubject.asObservable(); // Observable for other components to subscribe

  private ittIdSubject = new BehaviorSubject<number | null>(null); // Initial value is null
  ittId$ = this.ittIdSubject.asObservable(); // Observable for other components to subscribe

  private tenderTypeSubject = new BehaviorSubject<string | null>(null);
  tenderType$ = this.tenderTypeSubject.asObservable();

  private projectIdentifierSubject = new BehaviorSubject<string | null>(null);
  projectIdentifier$ = this.projectIdentifierSubject.asObservable();

  setProjectIdentifier(projectIdentifier: string): void {
    this.projectIdentifierSubject.next(projectIdentifier);
  }

  getProjectIdentifier(): string | null {
    return this.projectIdentifierSubject.getValue();
  }

  // Set the eoi id
  setEoiId(eoiId: any): void {
    localStorage.setItem('eoi_id', eoiId);
    this.eoiIdSubject.next(eoiId);
  }

  // Get the current value of eoi id
  getEoiId(): number | null {
    return this.eoiIdSubject.getValue();
  }

  // Set the PQQ/RFP id
  setIttId(ittId: any): void {
    this.ittIdSubject.next(ittId);
  }
  // Get the current value of PQQ/RFP id
  getIttId(): number | null {
    return this.ittIdSubject.getValue();
  }

  getEOIReceiverOrganizationDetails(params: any): Observable<any> {
    const httpParams = new HttpParams({ fromObject: params });
    return this.http.get(`${this.apiUrl}/e-o-i-search-organization`, {
      params: httpParams,
    });
  }

  fetchEoiStep1Data(): Observable<any> {
    const eoi_id = localStorage.getItem('eoi_id');
    console.log('The eoi id is:', eoi_id);
    const projectId = this.getProjectIdService.getProjectId();
    if (eoi_id && projectId) {
      return this.http.get(
        `${this.apiUrl}/eoi-submitted-initiation-data/${projectId}/${eoi_id}`
      );
    }
  }

  storeEOIStep1Data(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(`${this.apiUrl}/e-o-i-wp-details/${projectId}`, data);
  }

  storeEOIStep2Data(
    data: any,
    projectId: number,
    eoiId: number
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/e-o-i-organization-details/${projectId}/${eoiId}`,
      data
    );
  }

  storeEOIStep3Data(data: any, eoiId: number): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(
      `${this.apiUrl}/e-o-i-submission-details/${projectId}/${eoiId}`,
      data
    );
  }

  getProjectsRequested(): Observable<any> {
    return this.http.post(`${this.apiUrl}/projects/tendering-requests`, {});
  }

  getProjectEOIListing(): Observable<any> {
    let projectId: any;
    this.getProjectIdService.projectId$.subscribe((id) => {
      projectId = id;
      if (!projectId) {
        projectId = this.getProjectIdService.getProjectId();
      }
    });
    return this.http.post(
      `${this.apiUrl}/get-single-project-eoi-requests/${projectId}`,
      {}
    );
  }

  getSubmittedEoiInitiationData(
    projectId: number,
    eoiId: number
  ): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/eoi-submitted-initiation-data/${projectId}/${eoiId}`
    );
  }

  // get current stage with required data
  getEOICurrentStageData(
    projectId: number,
    selectedEoiId: number
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/get-eoi-current-stage/${projectId}/${selectedEoiId}`,
      {}
    );
  }

  // Fetch the current eoi on the applicant side
  getCurrentEoiResponse(projectId: number, eoiId: number): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/eoi/${eoiId}/current-eoi-response`
    );
  }
  // Check weather applicant submitted response for Eoi or not
  checkEoiResponseStatus(
    projectId: number,
    eoiId: number,
    responseId: number
  ): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/eoi/${eoiId}/response/${responseId}/submitted`
    );
  }

  /**
   * ####################################
   * EXPRESSION OF INTEREST SECTION END
   * ####################################
   */

  /**
   * ##################################
   * PRE QUALIFICATION QUESTIONNAIRE START
   * ##################################
   */

  // Set the pqqId
  setpqqId(pqqId: any): void {
    this.pqqIdSubject.next(pqqId);
  }
  // Get the current value of eoiId
  getPqqId(): number | null {
    return this.pqqIdSubject.getValue();
  }

  projectPQQInitiateStep1(data: any): Observable<any> {
    const pqq_id = this.getPqqId();

    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(
      `${this.apiUrl}/p-q-q-organization-details/${projectId}/${pqq_id}`,
      data
    );
  }
  fetchModuleSelectedWorkPackages(id, projectId, type): Observable<any> {
    switch (type) {
      case 'eoi':
        return this.http.get(
          `${this.apiUrl}/eoi-submitted-initiation-data/${projectId}/${id}`
        );
        break;

      case 'pqq':
        break;

      default:
        break;
    }
  }

  getProjectsWithPQQ(): Observable<any> {
    return this.http.post(`${this.apiUrl}/get-projects-with-pqq-requests`, {});
  }

  projectPqqInitiateStep2(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(`${this.apiUrl}/p-q-q-wp-details/${projectId}`, data);
  }
  projectPqqInitiateStep3(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(
      `${this.apiUrl}/pqq/submission/details/${projectId}`,
      data
    );
  }

  private isEditSubject = new BehaviorSubject<boolean>(false);
  private isAppliedSubject = new BehaviorSubject<boolean>(false);

  // Expose the `isEdit$` observable to allow subscriptions
  isEdit$ = this.isEditSubject.asObservable();
  isApplied$ = this.isAppliedSubject.asObservable();

  // Method to toggle the `isEdit` state
  toggleEdit(): void {
    const currentState = this.isEditSubject.value;
    this.isEditSubject.next(!currentState);
  }

  // Method to set the `isEdit` state directly
  setEditState(state: boolean): void {
    this.isEditSubject.next(state);
  }

  // Method to set the `isEdit` state directly
  setIsAppliedState(state: boolean): void {
    this.isAppliedSubject.next(state);
  }

  // Method to get the current `isEdit` state
  getEditState(): boolean {
    return this.isEditSubject.value;
  }

  // Method to get the current `isEdit` state
  getAppliedState(): boolean {
    return this.isAppliedSubject.value;
  }

  // Method to get the current `isEdit` state
  getCurrentProjectPQQListing(
    projectId: number,
    type: string
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/projects/tendering-request/${projectId}/type/${type}`,
      {}
    );
  }

  checkPqqResponseStatus(
    projectId: number,
    pqqId: number,
    pqqResponseId: number
  ): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/pqq/${pqqId}/response/${pqqResponseId}/submitted`
    );
  }

  /**
   * ##################################
   * PRE QUALIFICATION QUESTIONNAIRE END
   * ##################################
   */

  /**
   * ##################################
   * Request For Proposal Start
   * ##################################
   */

  getIttDeveloperListing(projectId: number, isRFPorRFQ: string): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/listing/${isRFPorRFQ}`
    );
  }

  storeDeveloperITTWorkPackagesData(
    data: any,
    projectId: number
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/store-work-packages-details`,
      data
    );
  }

  storeDeveloperITTOrganizationDetailsData(
    data: any,
    projectId: number
  ): Observable<any> {
    const ittId = this.getIttId();
    return this.http.put(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/${ittId}/store-organization-details`,
      data
    );
  }

  storeDeveloperITTSubmissionGuidelinesData(
    data: any,
    projectId: number
  ): Observable<any> {
    // TODO: get itt id in component and send here
    const ittId = this.getIttId();
    return this.http.put(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/${ittId}/store-submission-details`,
      data
    );
  }

  storeDeveloperITTContractData(data: any, projectId: number): Observable<any> {
    const ittId = this.getIttId();
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/${ittId}/store-contract-details`,
      data
    );
  }

  storeDeveloperITTKeyDatesData(data: any, projectId: number): Observable<any> {
    const ittId = this.getIttId();
    return this.http.put(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/${ittId}/store-dates`,
      data
    );
  }

  storeTechnicalEvaluationCriteria(data: any, projectId: number) {
    const ittId = this.getIttId();
    return this.http.put(
      `${this.apiUrl}/projects/${projectId}/tendering/rfp/${ittId}/store-technical-evaluation-criteria`,
      data
    );
  }

  storeCommercialEvaluationCriteria(data: any, projectId: number) {
    const ittId = this.getIttId();
    return this.http.put(
      `${this.apiUrl}/projects/${projectId}/tendering/rfp/${ittId}/store-commercial-evaluation-criteria`,
      data
    );
  }

  // Developer side data with details on which step we are
  ittDeveloperDataWithStep(projectId: number, ittId: number) {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/${ittId}/current-stage`
    );
  }

  // Developer side data without details on which step we are
  SubmittedInitiationData(projectId: number | string, ittId: number | string) {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/${ittId}`
    );
  }

  // Developer side data without details on which step we are
  rfpAwardContractToApplicantOrganization(
    projectId: number,
    rfpId: number,
    rfpResponseId: number
  ) {
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfp/${rfpId}/award/contract/${rfpResponseId}`,
      {}
    );
  }

  ittInternalApprovals(
    projectId: number | string,
    modelType: string,
    modelId: number | string
  ): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/${modelType}/${modelId}/internal-approval/get-responses`
    );
  }

  sendIttForApproval(
    data: any,
    projectId: number,
    modelType: string,
    modelId: number
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/${modelType}/${modelId}/internal-approval/initiate`,
      data
    );
  }

  sendIttApprovalResponse(
    data: any,
    projectId: number,
    modelType: string,
    modelId: number
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/${modelType}/${modelId}/internal-approval/store-response`,
      data
    );
  }

  initiateITT(
    projectId: number | string,
    modelId: number | string
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/${modelId}/initiate`,
      {}
    );
  }

  checkRfpResponseStatus(
    projectId: number,
    rfpId: number,
    rfpResponseId: number
  ): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/rfp/${rfpId}/response/${rfpResponseId}/submitted`
    );
  }

  /**
   * ##################################
   * Request For Proposal END
   * ##################################
   */

  /**
   * ##################################
   * Tenderer RFP START
   * ##################################
   */

  fetchIttTendererListing(rfpOrRfq: string): Observable<any> {
    
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/${rfpOrRfq}/requests`
    );
  }

  // TODO: why created below two APIs and where to use @ismail please view these below two
  fetchIttDetailOnTendererSide(): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/response/listing`
    );
  }

  fetchTendererIttDetail(): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/response/listing`
    );
  }

  addITTConsultancyService(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    const ittId = this.getIttId();
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/${ittId}/response/store-consultancy-services-details`,
      data
    );
  }

  fetchITTConsultancyService(): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    const ittId = this.getIttId();
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/${ittId}/response/store-consultancy-services-details`
    );
  }

  addITTConstructionWork(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    const ittId = this.getIttId();
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/${ittId}/response/store-construction-works-details`,
      data
    );
  }

  updateTendererITT(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/response/store-consultancy-services-details`,
      data
    );
  }

  // Tenderer/Applicant side technical submission service
  addRFPApplicantTechnicalSubmissionDetails(
    data: any,
    ittId: number
  ): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/${ittId}/response/store-rfp-response`,
      data
    );
  }

  // Tenderer side Conclusion wizard submission service to store response
  addITTConclusion(data: any, ittId: number): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/${ittId}/response/store-conclusion-details`,
      data
    );
  }
  // Tenderer side Commercial submission wizard submission service to store response
  addCommercialSubmission(data: any, ittId: number): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/${ittId}/response/store-commercial-submission`,
      data
    );
  }

  setTenderType(tenderType: string): void {
    this.tenderTypeSubject.next(tenderType);
  }

  getTenderType(): string | null {
    return this.tenderTypeSubject.getValue();
  }

  calculateTechnicalResult(data, ittId, responseId): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfp/${ittId}/response/${responseId}/technical/score`,
      data
    );
  }

  calculateCommercialResult(data, ittId, responseId): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfp/${ittId}/response/${responseId}/commercial/score`,
      data
    );
  }

  submitTechnicalResult(data, ittId, responseId): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfp/${ittId}/response/${responseId}/technical/save`,
      data
    );
  }

  submitCommercialResult(data, ittId, responseId): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfp/${ittId}/response/${responseId}/commercial/save`,
      data
    );
  }

  getIttTechnicalResult(ittId, responseId): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/rfp/${ittId}/response/${responseId}/technical/evaluation`
    );
  }

  getIttCommercialResult(ittId, responseId): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/rfp/${ittId}/response/${responseId}/commercial/evaluation`
    );
  }

  // Get the submitted response of tenderer on Tenderer side only
  getIttTendererResponse(ittId: number): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/${ittId}/applicant/response`
    );
  }

  // Get the submitted response of tenderer on Tenderer side only
  developerViewIttTendererResponse(ittId: number, ittResponseId: number): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/itt/${ittId}/response/${ittResponseId}/show`
    );
  }

  // Update RFP Tenderer Response Status to submitted
  updateRFPTendererResponseStatus(
    projectId: number,
    rFPId: number,
    rFPResponseId: number
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfp/${rFPId}/response/${rFPResponseId}/submit`,
      {}
    );
  }

  /**
   * ##################################
   * Tenderer ITT END
   * ##################################
   */

  /**
   * ##################################
   * ITT/RFP CLARIFICATION START
   * ##################################
   */
  // For both Developer/Client and Applicant
  getRfpApplicantQueries(projectId: number, rfpId: number): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/rfp/${rfpId}/clarifications/queries`
    );
  }

  // For both Developer/Client and Applicant
  getRfpApplicantQueriesResponses(
    projectId: number,
    rfpId: number
  ): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/rfp/${rfpId}/clarifications/queries/answer`
    );
  }

  // For Developer to submit response for one or multiple queries
  storeRfpQueriesResponse(
    data: any,
    projectId: number,
    rfpId: number
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfp/${rfpId}/clarifications/queries/answer/store`,
      data
    );
  }

    // For Developer to create addendum
    storeRfpAddendem(
      data: any,
      projectId: number,
      rfpId: number
    ): Observable<any> {
      return this.http.post(
        `${this.apiUrl}/projects/${projectId}/tendering/rfp/${rfpId}/clarifications/addendem/store`,
        data
      );
    }

  // For Applican to submit a query
  storeRfpQuery(data: any, projectId: number, rfpId: number): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfp/${rfpId}/clarifications/queries/store`,
      data
    );
  }

  getRFPAddendums(projectId: number, rfpId: number): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/rfp/${rfpId}/clarifications/addendums`
    );
  }

  /**
   * ##################################
   * ITT/RFP CLARIFICATION END
   * ##################################
   */
}
